import React from "react";
import {
  BrowserRouter as Router,
  Route,
  Redirect,
  Switch,
} from "react-router-dom";
import CreateQuizPage from "./pages/create-quiz/create-quiz-page";
import AbsiteDiagnosticPage from "./pages/absite-diagnostic/absite-diagnostic";
import { HIDE_ABSITE_DIAGNOSTIC } from "./constants/incidentFlags";
import AbsiteDiagnosticReportPage from "./pages/absite-diagnostic/absite-diagnostic-report";
import ExamPage from "./pages/exam/exam";
import ExamResult from "./pages/exam-result/exam-result";
import OverallPerformance from "./pages/overall-performance/overall-performance";
import QuizHistory from "./pages/quiz-history/quiz-history";
import MentorshipsPage from "./pages/mentorships/mentorships";
import MentorshipStudentPage from "./pages/mentorships/mentorship-student";
import LiveSessionBuilder from "./pages/live-session/live-session-builder";
import LivePresenter from "./pages/live-session/live-presenter";
import LivePhone from "./pages/live-session/live-phone";
import { CONSTANTS } from "./constants/app-constant";
import Login from "./pages/login/login";
import ForgetPwd from "./pages/login/forgetPwd";
import { AuthRedirect } from "./pages/login/googleAuthRedirect";
import Register from "./pages/login/register";
import SubscriptionsView from "./pages/subscriptions/subscriptions";
import AdminPlanView from "./pages/admin/plans";
import AdminView from "./pages/admin/admin";
import FeedbackView from "./pages/feedback/feedback";
import FeedbackDashboardView from "./pages/feedback-dashboard/feedback-dashboard";
import ProgramMetricsPage from "./pages/program-metrics/program-metrics";
import ReviewQueuePage from "./pages/review-queue/review-queue";
import ReviewStatusPage from "./pages/review-status/review-status";
import ReviewPublishPage from "./pages/review-publish/review-publish";
import ContentQualityPage from "./pages/content-quality/content-quality";
import AbsiteProgramCodeRequestPage from "./pages/absite-program-code-request/absite-program-code-request";
import AbsiteProgramCodeRegistryPage from "./pages/absite-program-code-registry/absite-program-code-registry";
import NotesToQuizClaimPage from "./pages/notes-to-quiz/notes-to-quiz-claim";
import Refund from './pages/admin/refund';
import QlinkPage from './pages/qlink/qlink';
import UserSettingsPage from './pages/user-settings/user-settings';
import PrivacyPolicy from './pages/privacy-policy/privacy-policy';
import TermsOfService from './pages/terms/terms';
import DailyAbsiteQuestionPage from './pages/daily-absite-question/daily-absite-question';
import SupportPage from './pages/support/support';
import GlDeckPage from './pages/gl-deck/gl-deck-page';
import RouteMetaManager from './components/route-meta/route-meta';
import DriveModePage from "./pages/drive-mode/drive-mode-page";
import { driveModeAvailable } from "./components/drive-mode-screen/drive-mode-flags";

// Q7-T1 live polling session — build-time UI flag hiding the entry (§4.4). The
// server independently 404s every route until qlearn.live-session.enabled, so the
// feature is dark twice over.
const LIVE_SESSION_UI_ENABLED = process.env.REACT_APP_ENABLE_LIVE_SESSION === "true";

const AppRoutes = () => {

  return (
    <div>
      <Router>
        {/* Route-level noindex for authed/app-internal routes (LEG-1 §5.S3);
            root/login stay indexable. */}
        <RouteMetaManager />
        <FeedbackView></FeedbackView>
        <Switch>
          {driveModeAvailable() && (
            <Route exact path="/drive">
              <DriveModePage />
            </Route>
          )}
          <Route exact path="/create-quiz">
            <CreateQuizPage />
          </Route>
          <Route exact path="/absite-diagnostic">
            {HIDE_ABSITE_DIAGNOSTIC ? (
              <Redirect to="/create-quiz" />
            ) : (
              <AbsiteDiagnosticPage />
            )}
          </Route>
          <Route exact path="/absite-diagnostic/:quizId/report">
            <AbsiteDiagnosticReportPage />
          </Route>
          <Route exact path="/absite-program-code-request">
            <AbsiteProgramCodeRequestPage />
          </Route>
          <Route exact path="/exam/:examId/:currentQuestion">
            <ExamPage />
          </Route>
          {/* :review only ever means the literal review mode — a free-form
              third segment let /exam/<id>/1/anything flip review UI on
              (answer-key exposure; adversarial gate on #517). */}
          <Route exact path="/exam/:examId/:currentQuestion/:review(review)">
            <ExamPage />
          </Route>
          <Route exact path="/exam/:examId/:currentQuestion/tutor/:tutor">
            <ExamPage />
          </Route>
          <Route exact path="/exam/:examId/:currentQuestion/tutor/:tutor/guide/:guide">
            <ExamPage />
          </Route>
          <Route exact path="/question/view/:qId">
            <ExamPage />
          </Route>
          <Route exact path="/qlink/preview/:qlinkId">
            <ExamPage />
          </Route>
          <Route exact path="/result/:examId">
            <ExamResult />
          </Route>
          <Route exact path="/overall-performance">
            <OverallPerformance />
          </Route>
          <Route exact path="/quiz-history">
            <QuizHistory />
          </Route>
          {CONSTANTS.mentorshipsEnabled && (
            <Route exact path="/mentorships">
              <MentorshipsPage />
            </Route>
          )}
          {CONSTANTS.mentorshipsEnabled && (
            <Route exact path="/mentorships/students/:studentUserId">
              <MentorshipStudentPage />
            </Route>
          )}
          {LIVE_SESSION_UI_ENABLED && (
            <Route exact path="/live-session/build">
              <LiveSessionBuilder />
            </Route>
          )}
          {LIVE_SESSION_UI_ENABLED && (
            <Route exact path="/live/present/:sessionId">
              <LivePresenter />
            </Route>
          )}
          {LIVE_SESSION_UI_ENABLED && (
            <Route exact path="/live/:shortCode" render={({ match }) => <LivePhone key={match.params.shortCode} />} />
          )}
          <Route exact path="/login">
            <Login />
          </Route>
          <Route exact path="/daily-absite-question">
            <DailyAbsiteQuestionPage />
          </Route>
          <Route exact path="/socialAuth">
            <AuthRedirect />
          </Route>
          <Route exact path="/subscriptions">
            <SubscriptionsView />
          </Route>
          <Route exact path="/plans">
            <SubscriptionsView isPlan={true} />
          </Route>
          <Route exact path="/payment/success/:bookingId">
            <SubscriptionsView />
          </Route>
          <Route exact path="/payment/failure/:bookingId">
            <SubscriptionsView />
          </Route>
          <Route exact path="/manage-plans">
            <AdminPlanView />
          </Route>
          <Route exact path="/refund">
            <Refund />
          </Route>
          <Route exact path="/reset-password">
            <ForgetPwd hasId={true} />
          </Route>
          <Route exact path="/forget-password">
            <ForgetPwd hasId={false} />
          </Route>
          <Route exact path="/verify-user/:id">
            <Register verify={true} />
          </Route>
          {/* E1 (design review): email/password registration was retired. The
              form created LOCAL accounts that then could not sign in through the
              Google/Phone-only /login (the email tab was deliberately removed in
              8e133f7). /register keeps no in-app entry point, so redirect stale
              external links to /login instead of rendering a dead-end signup or
              404ing. (/verify-user still uses <Register verify /> for existing
              verification emails and is unaffected.) */}
          <Route exact path="/register">
            <Redirect to="/login" />
          </Route>
          <Route exact path="/admin-portal">
            <AdminView verify={false} />
          </Route>
          <Route exact path="/feedback-dashboard">
            <FeedbackDashboardView />
          </Route>
          <Route exact path="/program-metrics">
            <ProgramMetricsPage />
          </Route>
          <Route exact path="/program-codes">
            <AbsiteProgramCodeRegistryPage />
          </Route>
          <Route exact path="/admin/visuals-review">
            <Redirect to="/admin/review-queue" />
          </Route>
          <Route exact path="/admin/review-queue">
            <ReviewQueuePage />
          </Route>
          <Route exact path="/admin/review-status">
            <ReviewStatusPage />
          </Route>
          <Route exact path="/admin/review-publish">
            <ReviewPublishPage />
          </Route>
          <Route exact path="/admin/content-quality">
            <ContentQualityPage />
          </Route>
          <Route exact path="/notes-to-quiz/:token">
            <NotesToQuizClaimPage />
          </Route>
          <Route exact path="/qlink">
            <QlinkPage />
          </Route>
          <Route exact path="/user-settings">
            <UserSettingsPage />
          </Route>
          <Route exact path="/privacy-policy">
            <PrivacyPolicy />
          </Route>
          <Route exact path="/terms">
            <TermsOfService />
          </Route>
          <Route exact path="/support">
            <SupportPage />
          </Route>
          {/* GL-5b due-queue (dark). The page redirects away when the GL
              flag/audience are off, so flags-off adds no visible surface. */}
          <Route exact path="/gl/deck">
            <GlDeckPage />
          </Route>
          <Route path="/" exact>
            {/* A signed-in student visiting the bare domain was bounced to
                the LOGIN page — reading as "I got logged out" every time
                (UX-sweep slice-2 P1 ROOT-1). Create-quiz is the app's
                de-facto home until a real dashboard exists. */}
            {window.localStorage.getItem("token") ? (
              <Redirect to="/create-quiz" />
            ) : (
              <Redirect to="/login" />
            )}
          </Route>
        </Switch>
      </Router>
    </div>
  );
};

export default AppRoutes;
